import axios, { AxiosInstance } from 'axios';
import type {
  User,
  AuthResponse,
  Raffle,
  RaffleDetail,
  RaffleEntryRequest,
  Transaction,
  ReferralStats,
  ReferralUserInfo,
  DepositRequest,
  Jackpot,
  JackpotBetRequest,
  PrizeItem,
  TicketImage,
  UserTicket,
  TicketBuyRequest,
  AdminStats,
  GamificationProfile,
  LeaderboardEntry,
} from '@/types';

class ApiClient {
  private client: AxiosInstance;
  private initData: string = '';

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add init data
    this.client.interceptors.request.use((config) => {
      if (this.initData) {
        config.headers['X-Telegram-Init-Data'] = this.initData;
      }
      return config;
    });
  }

  setInitData(initData: string) {
    this.initData = initData;
  }

  // Users
  async getCurrentUser(): Promise<User> {
    const { data } = await this.client.get<User>('/users/me');
    return data;
  }

  async getUserBalance(): Promise<{ balance: number; referral_earnings: number }> {
    const { data } = await this.client.get('/users/balance');
    return data;
  }

  async getReferralStats(): Promise<ReferralStats> {
    const { data } = await this.client.get<ReferralStats>('/users/referral-stats');
    return data;
  }

  async getReferrals(): Promise<ReferralUserInfo[]> {
    const { data } = await this.client.get<ReferralUserInfo[]>('/users/referrals');
    return data;
  }

  // Raffles
  async getRaffles(): Promise<Raffle[]> {
    const { data } = await this.client.get<Raffle[]>('/raffles/');
    return data;
  }

  async getRaffle(id: number): Promise<RaffleDetail> {
    const { data } = await this.client.get<RaffleDetail>(`/raffles/${id}`);
    return data;
  }

  async enterRaffle(request: RaffleEntryRequest): Promise<any> {
    const { data } = await this.client.post('/raffles/enter', request);
    return data;
  }

  async getCompletedRaffles(limit: number = 20): Promise<Raffle[]> {
    const { data } = await this.client.get<Raffle[]>(`/raffles/completed?limit=${limit}`);
    return data;
  }

  // Tickets
  async getAvailableTickets(): Promise<TicketImage[]> {
    const { data } = await this.client.get<TicketImage[]>('/tickets/available');
    return data;
  }

  async buyTicket(request: TicketBuyRequest): Promise<UserTicket> {
    const { data } = await this.client.post<UserTicket>('/tickets/buy', request);
    return data;
  }

  async getMyTickets(unusedOnly: boolean = false): Promise<UserTicket[]> {
    const { data } = await this.client.get<UserTicket[]>(
      `/tickets/my${unusedOnly ? '?unused_only=true' : ''}`
    );
    return data;
  }

  async getRaffleHistory(): Promise<any[]> {
    const { data } = await this.client.get('/raffles/history/me');
    return data;
  }

  // Transactions
  async getTransactions(limit: number = 50): Promise<Transaction[]> {
    const { data } = await this.client.get<Transaction[]>(`/transactions/?limit=${limit}`);
    return data;
  }

  async createTestDeposit(request: DepositRequest): Promise<any> {
    const { data } = await this.client.post('/transactions/deposit/test', request);
    return data;
  }

  // Jackpot
  async getCurrentJackpot(): Promise<Jackpot> {
    const { data } = await this.client.get<Jackpot>('/jackpot/current');
    return data;
  }

  async placeJackpotBet(request: JackpotBetRequest): Promise<any> {
    const { data } = await this.client.post('/jackpot/bet', request);
    return data;
  }

  async checkJackpotTimer(): Promise<any> {
    const { data } = await this.client.post('/jackpot/check-timer');
    return data;
  }

  async addTestBotsToJackpot(betAmounts: number[]): Promise<any> {
    const { data } = await this.client.post('/jackpot/admin/add-test-bots', { bet_amounts: betAmounts });
    return data;
  }

  // Web Auth
  async webRegister(email: string, password: string, username?: string): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/auth/web/register', { email, password, username });
    return data;
  }

  async webLogin(email: string, password: string): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/auth/web/login', { email, password });
    return data;
  }

  async telegramWidgetLogin(widgetData: Record<string, unknown>): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/auth/telegram/widget', widgetData);
    return data;
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/signout');
  }

  // Admin
  async getAllRaffles(): Promise<Raffle[]> {
    const { data } = await this.client.get<Raffle[]>('/admin/raffles/');
    return data;
  }

  async createRaffle(request: { title: string; description: string; photo_url?: string; prizes?: PrizeItem[]; entry_price: number; max_participants: number }): Promise<Raffle> {
    const { data } = await this.client.post<Raffle>('/admin/raffles/', request);
    return data;
  }

  async startRaffle(raffleId: number): Promise<any> {
    const { data } = await this.client.post(`/admin/raffles/${raffleId}/start`);
    return data;
  }

  async drawRaffle(raffleId: number): Promise<any> {
    const { data } = await this.client.post(`/admin/raffles/${raffleId}/draw`);
    return data;
  }

  async deleteRaffle(raffleId: number): Promise<any> {
    const { data } = await this.client.delete(`/admin/raffles/${raffleId}`);
    return data;
  }

  async getAdminStats(): Promise<AdminStats> {
    const { data } = await this.client.get<AdminStats>('/admin/raffles/stats');
    return data;
  }

  async getGamification(): Promise<GamificationProfile> {
    const { data } = await this.client.get<GamificationProfile>('/users/me/gamification');
    return data;
  }

  async getLeaderboard(limit = 50): Promise<LeaderboardEntry[]> {
    const { data } = await this.client.get<LeaderboardEntry[]>(`/users/leaderboard?limit=${limit}`);
    return data;
  }
}

export const api = new ApiClient();
